<script setup lang="ts">
  import { ref, unref, watch } from 'vue';
  import { useRouter } from 'vue-router';

  import type { DocumentSendRecipientType } from '@/entities/DocumentSend';
  import PrinterIcon from '@/shared/assets/icons/PrinterIcon.svg';
  import SelectDocumentIcon from '@/shared/assets/icons/SelectDocumentIcon.svg';
  import TrashIcon from '@/shared/assets/icons/TrashIcon.svg';
  import { ColoredButton, DefaultTable, IconButton, TableHeader } from '@/shared/ui';

  import { DocumentSelectionModal } from '@/features/Document/@x/DocumentSendControl';

  import { useDocumentSendPdf } from '../model/useDocumentSendPdf';
  import { useDocumentSendRecipientReport } from '../model/useDocumentSendRecipientReport';
  import { useDocumentSendReport } from '../model/useDocumentSendReport';
  import DocumentSendRecipientReportResult from './DocumentSendRecipientReportResult.vue';
  import DocumentSendRecipientSelector from './DocumentSendRecipientSelector.vue';
  import DocumentSendReportResult from './DocumentSendReportResult.vue';

  type ReportMode = 'document' | 'recipient';

  const router = useRouter();
  const selectedMode = ref<ReportMode>('document');

  const {
    canGenerateReport,
    closeDocumentModal,
    generateReport,
    handleDocumentSelected,
    isDocumentModalOpen,
    loadingReport,
    openDocumentModal,
    removeSelectedDocument,
    resetState: resetDocumentReportState,
    report,
    selectedDocument,
  } = useDocumentSendReport();

  const {
    canGenerateReport: canGenerateRecipientReport,
    clearSelectedRecipient,
    filteredRecipients,
    generateReport: generateRecipientReport,
    isLoadingRecipients,
    loadingReport: loadingRecipientReport,
    report: recipientReport,
    resetState: resetRecipientReportState,
    searchTerm,
    selectRecipient,
    selectedRecipient,
    selectedRecipientType,
    setRecipientType,
    updateSearchTerm,
  } = useDocumentSendRecipientReport();

  const { isGenerating, printReportByDocument, printReportByRecipient } = useDocumentSendPdf();

  const handleRecipientTypeChange = (recipientType: DocumentSendRecipientType) => {
    setRecipientType(recipientType);
  };

  const handleGenerateReport = () => {
    if (selectedMode.value === 'recipient') {
      void generateRecipientReport();
      return;
    }

    void generateReport();
  };

  watch(selectedMode, (next, previous) => {
    if (next === previous) {
      return;
    }

    resetDocumentReportState();
    resetRecipientReportState();
  });

  const handleCancel = () => {
    const navigation = router.push('/gestao-documentos/controle-envio');
    navigation?.catch(() => undefined);
  };

  const handleNewQuery = () => {
    resetDocumentReportState();
    resetRecipientReportState();
  };

  const handlePrint = () => {
    const currentReport = unref(report);
    if (currentReport) {
      void printReportByDocument(currentReport.document.documentRevisionId);
      return;
    }

    const currentRecipientReport = unref(recipientReport);
    if (!currentRecipientReport) {
      return;
    }

    const { recipientType, protheusCode, protheusStore } = currentRecipientReport.recipient;
    void printReportByRecipient({ recipientType, protheusCode, protheusStore });
  };
</script>

<template>
  <section
    class="flex flex-col gap-6 text-gray-07"
    data-testid="document-send-report-page"
  >
    <h1 class="text-left text-xl/7.5 font-semibold">
      Lista de Envio
    </h1>

    <template v-if="report || recipientReport">
      <div class="flex justify-end gap-4">
        <button
          type="button"
          class="rounded-lg border border-gray-03 px-4 py-3 text-sm font-medium text-gray-03"
          data-testid="document-send-report-new-query-button"
          @click="handleNewQuery"
        >
          Nova consulta
        </button>
        <ColoredButton
          text="Imprimir"
          :icon="PrinterIcon"
          icon-alt="Imprimir"
          :loading="isGenerating"
          data-testid="document-send-report-print-button"
          @click="handlePrint"
        />
      </div>

      <DocumentSendReportResult
        v-if="report"
        :report="report"
      />
      <DocumentSendRecipientReportResult
        v-else-if="recipientReport"
        :report="recipientReport"
      />
    </template>

    <template v-else>
      <div class="overflow-hidden rounded-2xl border border-gray-02 bg-white-01">
        <section class="flex flex-col gap-4 border-b border-gray-02 p-6">
          <span class="text-sm font-semibold text-gray-07">Gerar lista por:</span>

          <div class="flex flex-wrap gap-8">
            <label class="flex items-center gap-2 text-sm text-gray-07">
              <input
                v-model="selectedMode"
                type="radio"
                name="report-mode"
                value="document"
                data-testid="report-mode-document"
              >
              Documento
            </label>

            <label class="flex items-center gap-2 text-sm text-gray-07">
              <input
                v-model="selectedMode"
                type="radio"
                name="report-mode"
                value="recipient"
                data-testid="report-mode-recipient"
              >
              Destinatário
            </label>
          </div>
        </section>

        <section
          v-if="selectedMode === 'document'"
          class="flex flex-col gap-6 p-6"
        >
          <div class="flex flex-col gap-3">
            <span class="text-sm font-semibold text-gray-07">Documento:</span>
            <div class="max-w-max">
              <ColoredButton
                text="Selecionar Documento"
                :icon="SelectDocumentIcon"
                icon-alt="Selecionar documento"
                @click="openDocumentModal"
              />
            </div>
          </div>

          <DefaultTable v-if="selectedDocument">
            <thead>
              <tr>
                <TableHeader text="Prefixo" />
                <TableHeader text="Título" />
                <TableHeader text="Rev." />
                <TableHeader
                  text="Ações"
                  th-class="px-6 py-4.5 text-right text-xs font-semibold text-gray-07"
                />
              </tr>
            </thead>

            <tbody class="divide-y divide-gray-02 border-t border-gray-02 bg-white-01">
              <tr>
                <td class="pl-6 py-4.5 text-sm text-gray-07">
                  {{ selectedDocument.prefix }}
                </td>
                <td class="pl-6 py-4.5 text-sm text-gray-07">
                  {{ selectedDocument.title ?? '-' }}
                </td>
                <td class="pl-6 py-4.5 text-sm text-gray-07">
                  {{ selectedDocument.revision }}
                </td>
                <td class="px-6 py-4.5 text-right text-gray-xx-02">
                  <IconButton
                    :src="TrashIcon"
                    alt="Remover documento"
                    :on-click="removeSelectedDocument"
                  />
                </td>
              </tr>
            </tbody>
          </DefaultTable>
        </section>

        <section
          v-else
          class="p-6"
        >
          <DocumentSendRecipientSelector
            :filtered-recipients="filteredRecipients"
            :is-loading-recipients="isLoadingRecipients"
            :search-term="searchTerm"
            :selected-recipient="selectedRecipient"
            :selected-recipient-type="selectedRecipientType"
            @clear-selection="clearSelectedRecipient"
            @update:recipient-type="handleRecipientTypeChange"
            @update:search-term="updateSearchTerm"
            @select="selectRecipient"
          />
        </section>
      </div>

      <div class="flex justify-end gap-6 pr-4 pb-1.5">
        <button
          type="button"
          class="rounded-lg border border-gray-03 px-4 py-3 text-sm font-medium text-gray-03"
          data-testid="document-send-report-cancel-button"
          @click="handleCancel"
        >
          Cancelar
        </button>
        <ColoredButton
          text="Gerar lista"
          :disabled="selectedMode === 'document' ? !canGenerateReport : !canGenerateRecipientReport"
          :loading="selectedMode === 'document' ? loadingReport : loadingRecipientReport"
          data-testid="document-send-report-generate-button"
          @click="handleGenerateReport"
        />
      </div>
    </template>

    <DocumentSelectionModal
      v-if="isDocumentModalOpen"
      selection-mode="single"
      title="Selecionar Documento"
      show-sort
      :initial-selected-revision-id="selectedDocument?.documentRevisionId ?? null"
      @close="closeDocumentModal"
      @select="handleDocumentSelected"
    />
  </section>
</template>
